import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DatePickerField from '../UI/DatePickerField';
import {
  Search,
  ChevronDown,
  ChevronUp,
  X,
  Clock,
  Users,
  Shield,
  FileText,
  Send,
  CheckCircle,
  AlertCircle,
  Briefcase,
  Moon,
  Info,
  Check,
  CalendarRange,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import SearchableMultiCombobox from '../UI/SearchableMultiCombobox';
import FormSelect from '../UI/FormSelect';
import { toLocalISODate, parseLocalDate, formatDateShort, formatDateRange, formatTimeString12Hour } from '../../utils/dateUtils';

const NIGHT_HOURS = [];
for (let h = 18; h <= 23; h++) NIGHT_HOURS.push(`${String(h).padStart(2, '0')}:00`);
for (let h = 0; h <= 9; h++) NIGHT_HOURS.push(`${String(h).padStart(2, '0')}:00`);

const NIGHT_HOUR_OPTIONS = NIGHT_HOURS.map(t => ({ value: t, label: formatTimeString12Hour(t) }));

const calcShiftHours = (start, end, breakMin) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMins = sh * 60 + sm;
  let endMins = eh * 60 + em;
  if (endMins <= startMins) endMins += 24 * 60;
  const gross = (endMins - startMins) / 60;
  return { gross, net: gross - (breakMin || 0) / 60 };
};

// MOM Employment Act: workers must not work more than 6 continuous hours
// without a break of at least 45 min (we standardise to 60 min). With
// 1h breaks the max sustainable continuous span is:
//   K=0 -> 6h, K=1 -> 13h, K=2 -> 20h, K=3 -> 27h (cap).
//
// Mirrors NightworkRequest.calculate_break_duration on the backend so
// the banner shown to the requester matches what gets persisted.
//
// For Extension mode we account for the worker's day-shift continuity:
// if the NW shift starts within 1h of the standard 17:00 day-end, the
// worker has been continuously working since lunch end (13:00). If the
// gap is >= 1h that gap qualifies as a meal break and the NW shift
// restarts a fresh continuous block.
const DAY_SHIFT_END_MINUTES = 17 * 60;
const DAY_SHIFT_LUNCH_END_MINUTES = 13 * 60;
const MIN_GAP_TO_COUNT_AS_BREAK = 60;

const strictMomBreaks = (continuousMins) => {
  if (continuousMins <= 360) return 0;
  if (continuousMins <= 780) return 1;
  if (continuousMins <= 1200) return 2;
  return 3;
};

const calcMomBreakMinutes = (start, end, mode = 'standard') => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMins = sh * 60 + sm;
  let endMins = eh * 60 + em;
  if (endMins <= startMins) endMins += 24 * 60;
  const shiftMins = endMins - startMins;

  let continuousMins;
  if (mode === 'extension') {
    const gap = startMins - DAY_SHIFT_END_MINUTES;
    if (gap >= MIN_GAP_TO_COUNT_AS_BREAK) {
      continuousMins = shiftMins;
    } else {
      continuousMins = endMins - DAY_SHIFT_LUNCH_END_MINUTES;
    }
  } else {
    continuousMins = shiftMins;
  }
  return strictMomBreaks(continuousMins) * 60;
};

// ─── Safety Personnel Selector (Grab-style completion) ────────────────────────
const SafetyPersonnelSelect = ({ personnel, selected, onChange, loading }) => {
  const hasSelection = selected.length > 0;

  const toggle = (staffId) => {
    onChange(selected.includes(staffId) ? selected.filter(id => id !== staffId) : [...selected, staffId]);
  };

  return (
    <div className={`rounded-xl border-2 transition-colors ${hasSelection ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Shield className={`w-5 h-5 ${hasSelection ? 'text-green-600' : 'text-gray-400'}`} />
          <span className="text-sm font-medium text-gray-900">Safety Department</span>
        </div>
        {hasSelection && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            {selected.length} assigned
          </div>
        )}
      </div>
      <div className="p-2 max-h-48 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading safety personnel...</div>
        ) : personnel?.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500 text-center">No safety personnel found</div>
        ) : (
          (personnel || []).map(p => {
            const checked = selected.includes(p.staff_id);
            return (
              <label
                key={p.staff_id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-green-100' : 'hover:bg-gray-50'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p.staff_id)}
                  className="rounded border-gray-300 text-green-600 focus-visible:ring-green-500"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-900">{p.first_name} {p.last_name}</span>
                </div>
                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{p.staff_id}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main Form ────────────────────────────────────────────────────────────────
const NightworkRequestForm = ({ onSuccess, currentUser, editRequestId }) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const isEditMode = !!editRequestId;

  const [formData, setFormData] = useState({
    project: '',
    project_name: '',
    description: '',
    nightwork_mode: 'standard',
    recovery_type: 'full_day',
    date_selection_type: 'range',
    start_date: '',
    end_date: '',
    specific_dates: [],
    start_time: '23:00',
    end_time: '07:00',
    worker_ids: [],
    safety_ids: [],
    worker_date_overrides: {},
    safety_date_overrides: {},
  });
  const [editLoaded, setEditLoaded] = useState(false);
  const [checkedWorkers, setCheckedWorkers] = useState(new Set());
  const [showBulkDatePicker, setShowBulkDatePicker] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState(null);
  const [bulkEndDate, setBulkEndDate] = useState(null);

  const { data: editRequest } = useQuery({
    queryKey: ['nightwork-request', editRequestId],
    queryFn: () => staffAPI.getNightworkRequest(editRequestId),
    select: r => r.data,
    enabled: isEditMode && !editLoaded,
    staleTime: 15_000,
  });

  // Pending requests are edited in-place (no re-submit chain) — material
  // changes reset the approval chain server-side. Draft edits (plain v1 or
  // amendment) keep the existing save-then-submit flow.
  const isPendingEdit = isEditMode && ['pending_deputy_hod', 'pending_hod'].includes(editRequest?.status);

  useEffect(() => {
    if (editRequest && !editLoaded) {
      setFormData({
        project: editRequest.project || '',
        project_name: editRequest.project_code
          ? `${editRequest.project_code} - ${editRequest.project_name}`
          : '',
        description: editRequest.description || '',
        nightwork_mode: editRequest.nightwork_mode || 'standard',
        recovery_type: editRequest.recovery_type || 'full_day',
        date_selection_type: editRequest.date_selection_type || 'range',
        start_date: editRequest.start_date || '',
        end_date: editRequest.end_date || '',
        specific_dates: editRequest.specific_dates || [],
        start_time: editRequest.start_time?.slice(0, 5) || '23:00',
        end_time: editRequest.end_time?.slice(0, 5) || '07:00',
        worker_ids: (editRequest.assignments || [])
          .filter(a => a.role === 'worker' && a.is_active)
          .map(a => a.employee_staff_id),
        safety_ids: (editRequest.assignments || [])
          .filter(a => a.role === 'safety' && a.is_active)
          .map(a => a.employee_staff_id),
        worker_date_overrides: Object.fromEntries(
          (editRequest.assignments || [])
            .filter(a => a.role === 'worker' && a.is_active && (a.effective_from || a.effective_until))
            .map(a => [a.employee_staff_id, {
              start_date: a.effective_from || editRequest.start_date || '',
              end_date: a.effective_until || editRequest.end_date || '',
            }])
        ),
        safety_date_overrides: Object.fromEntries(
          (editRequest.assignments || [])
            .filter(a => a.role === 'safety' && a.is_active && (a.effective_from || a.effective_until))
            .map(a => [a.employee_staff_id, {
              start_date: a.effective_from || editRequest.start_date || '',
              end_date: a.effective_until || editRequest.end_date || '',
            }])
        ),
      });
      setEditLoaded(true);
    }
  }, [editRequest, editLoaded]);

  // Data fetches
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['active-projects'],
    queryFn: staffAPI.getActiveProjects,
    select: r => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: tradeGroups, isLoading: workersLoading } = useQuery({
    queryKey: ['nightwork-active-workers'],
    queryFn: staffAPI.getNightworkActiveWorkers,
    select: r => r.data,
    staleTime: 60_000,
  });

  const flatWorkers = useMemo(() =>
    (tradeGroups || []).flatMap(g =>
      g.workers.map(w => ({ ...w, trade_category: g.trade_category }))
    ),
    [tradeGroups]
  );

  const { data: safetyPersonnel, isLoading: safetyLoading } = useQuery({
    queryKey: ['nightwork-safety-personnel'],
    queryFn: staffAPI.getNightworkSafetyPersonnel,
    select: r => r.data,
    staleTime: 60_000,
  });

  // Project search
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const projectDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target)) setShowProjectDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    const q = projectSearch.toLowerCase();
    return projects.filter(p =>
      p.project_name?.toLowerCase().includes(q) || p.project_code?.toLowerCase().includes(q)
    );
  }, [projects, projectSearch]);

  const selectProject = (project) => {
    setFormData(prev => ({ ...prev, project: project.project_code, project_name: `${project.project_code} - ${project.project_name}` }));
    setShowProjectDropdown(false);
    setProjectSearch('');
  };

  const clearProject = () => {
    setFormData(prev => ({ ...prev, project: '', project_name: '' }));
  };

  // Shift calculation — break minutes derive from start/end + mode per
  // MOM rules, matching the backend so the requester sees the same number
  // that gets saved on the request. Extension mode rolls the day-shift
  // continuous block (since lunch end) into the calculation; standard mode
  // treats the NW shift as a fresh block.
  const breakMinutes = useMemo(
    () => calcMomBreakMinutes(formData.start_time, formData.end_time, formData.nightwork_mode),
    [formData.start_time, formData.end_time, formData.nightwork_mode]
  );
  const shiftInfo = useMemo(
    () => calcShiftHours(formData.start_time, formData.end_time, breakMinutes),
    [formData.start_time, formData.end_time, breakMinutes]
  );

  // Helper for the Extension-mode continuity hint shown beneath the banner.
  const extensionContinuity = useMemo(() => {
    if (formData.nightwork_mode !== 'extension' || !formData.start_time || !formData.end_time) {
      return null;
    }
    const [sh, sm] = formData.start_time.split(':').map(Number);
    const [eh, em] = formData.end_time.split(':').map(Number);
    const startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins <= startMins) endMins += 24 * 60;
    const gap = startMins - DAY_SHIFT_END_MINUTES;
    if (gap >= MIN_GAP_TO_COUNT_AS_BREAK) {
      return {
        kind: 'gap',
        gapHours: gap / 60,
      };
    }
    return {
      kind: 'continuous_from_lunch',
      continuousHours: (endMins - DAY_SHIFT_LUNCH_END_MINUTES) / 60,
    };
  }, [formData.nightwork_mode, formData.start_time, formData.end_time]);

  const saveMutation = useMutation({
    mutationFn: (data) =>
      isEditMode
        ? staffAPI.updateNightworkRequest(editRequestId, data)
        : staffAPI.createNightworkRequest(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['nightwork'] }),
  });

  const submitMutation = useMutation({
    mutationFn: (id) => staffAPI.submitNightworkRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      toast.success(isEditMode ? 'Amendment submitted for approval' : 'Nightwork request submitted for approval');
      onSuccess?.();
    },
  });

  const handleSubmit = async () => {
    try {
      const payload = buildPayload();

      if (isPendingEdit) {
        const res = await saveMutation.mutateAsync(payload);
        const { approval_reset, leave_warnings, restday_warnings } = res.data || {};
        if (approval_reset) {
          toast.warning('Changes saved. Schedule, personnel, or mode changed, so approvals were reset — the request re-enters the approval chain.');
        } else {
          toast.success('Changes saved.');
        }
        (leave_warnings || []).forEach(w => toast.warning(
          `${w.employee_name} has approved leave overlapping this request (${w.leave_type}).`
        ));
        (restday_warnings || []).forEach(w => toast.warning(
          `${w.employee_name} has a rest-day conflict on ${w.date}.`
        ));
        onSuccess?.();
        return;
      }

      const res = await saveMutation.mutateAsync(payload);
      const id = isEditMode ? editRequestId : res.data?.id;
      if (!id) {
        toast.error('Failed to retrieve request ID after saving. Please try again.');
        return;
      }
      await submitMutation.mutateAsync(id);
    } catch (err) {
      const msg = err.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const firstError = Object.values(msg).flat().join('; ');
        toast.error(firstError || 'Failed to submit');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Failed to submit');
      }
    }
  };

  const buildPayload = () => {
    const payload = {
      project: formData.project,
      description: formData.description,
      nightwork_mode: formData.nightwork_mode,
      recovery_type: formData.recovery_type,
      date_selection_type: formData.date_selection_type,
      start_time: formData.start_time,
      end_time: formData.end_time,
      worker_ids: formData.worker_ids,
      safety_ids: formData.safety_ids,
    };
    if (formData.date_selection_type === 'range') {
      payload.start_date = formData.start_date;
      payload.end_date = formData.end_date;
    } else {
      payload.specific_dates = formData.specific_dates;
    }
    if (Object.keys(formData.worker_date_overrides).length > 0) {
      payload.worker_date_overrides = formData.worker_date_overrides;
    }
    if (Object.keys(formData.safety_date_overrides).length > 0) {
      payload.safety_date_overrides = formData.safety_date_overrides;
    }
    return payload;
  };

  // Step validation
  const isStep1Valid = !!formData.project && formData.description.trim().length > 0;
  const isStep2Valid = formData.date_selection_type === 'range'
    ? !!formData.start_date && !!formData.end_date
    : formData.specific_dates.length > 0;
  const isStep3Valid = formData.worker_ids.length > 0;

  const canSubmit = isStep1Valid && isStep2Valid && isStep3Valid;

  // Specific date add/remove
  const toggleSpecificDate = (date) => {
    const iso = toLocalISODate(date);
    setFormData(prev => ({
      ...prev,
      specific_dates: prev.specific_dates.includes(iso)
        ? prev.specific_dates.filter(d => d !== iso)
        : [...prev.specific_dates, iso].sort(),
    }));
  };

  const nightworkDateCount = formData.date_selection_type === 'range'
    ? (formData.start_date && formData.end_date
        ? Math.ceil((new Date(formData.end_date) - new Date(formData.start_date)) / 86400000) + 1
        : 0)
    : formData.specific_dates.length;

  // ─── Render Stepper ─────────────────────────────────────────────
  const stepLabels = ['Project & Description', 'Dates & Times', 'Personnel', 'Review & Submit'];

  return (
    <div className="bg-white rounded-2xl shadow-sm border">
      {/* Stepper header */}
      <div className="border-b border-gray-100 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between sm:justify-center gap-2 sm:gap-3">
          {stepLabels.map((label, idx) => {
            const num = idx + 1;
            const isActive = step === num;
            const isCompleted = step > num;
            const canNavigate = num <= step || (
              (num <= 1) ||
              (num <= 2 && isStep1Valid) ||
              (num <= 3 && isStep1Valid && isStep2Valid) ||
              (num <= 4 && isStep1Valid && isStep2Valid && isStep3Valid)
            );
            return (
              <button
                key={num}
                type="button"
                onClick={() => canNavigate && setStep(num)}
                disabled={!canNavigate}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                  !canNavigate ? 'bg-gray-100 text-gray-300 cursor-not-allowed' :
                  isActive ? 'bg-blue-800 text-white' :
                  isCompleted ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-500'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                  isActive ? 'bg-white/20 text-white' :
                  isCompleted ? 'bg-blue-800 text-white' :
                  'bg-gray-300 text-white'
                }`}>
                  {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : num}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Step 1: Project & Description */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
              <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-blue-800">
                Use this when the shift will give workers <strong>rest or recovery the next morning or next day</strong> (AM Only or Full Day). Late hours with a normal full day after and no recovery are ordinary overtime — not nightwork.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Briefcase className="w-4 h-4 inline mr-1.5" />
                Project
              </label>
              {formData.project ? (
                <div className="flex items-center gap-2 input bg-gray-50">
                  <span className="flex-1 text-sm text-gray-900">{formData.project_name}</span>
                  <button type="button" onClick={clearProject} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative" ref={projectDropdownRef}>
                  <input
                    type="text"
                    placeholder={projectsLoading ? 'Loading projects...' : 'Search projects by name or code...'}
                    className="input w-full"
                    value={projectSearch}
                    onChange={e => { setProjectSearch(e.target.value); setShowProjectDropdown(true); }}
                    onFocus={() => setShowProjectDropdown(true)}
                  />
                  {showProjectDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {filteredProjects.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No projects found</div>
                      ) : (
                        filteredProjects.map(p => (
                          <div
                            key={p.project_code}
                            className="p-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                            onClick={() => selectProject(p)}
                          >
                            <div className="text-sm font-medium text-gray-900">{p.project_name}</div>
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{p.project_code}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <FileText className="w-4 h-4 inline mr-1.5" />
                Description of Works
              </label>
              <textarea
                rows={4}
                className="block w-full px-4 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-[border-color,box-shadow] duration-200 resize-none"
                placeholder="Describe the nightwork activities, scope, and any special requirements..."
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
        )}

        {/* Step 2: Dates & Times */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Nightwork Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nightwork Mode</label>
              <div className="flex gap-3">
                {[
                  { value: 'standard', label: 'Standard', desc: 'Replaces day shift' },
                  { value: 'extension', label: 'Extension', desc: 'After regular hours' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      const defaultRecovery = opt.value === 'extension' ? 'am_only' : 'full_day';
                      setFormData(prev => ({
                        ...prev,
                        nightwork_mode: opt.value,
                        recovery_type: defaultRecovery,
                      }));
                    }}
                    className={`tab-btn ${formData.nightwork_mode === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formData.nightwork_mode === 'extension'
                  ? 'Workers complete their day shift then extend into night hours.'
                  : 'Workers skip their day shift and work the night instead.'}
              </p>
            </div>

            {/* Date Selection Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Selection</label>
              <div className="flex gap-3">
                {[{ value: 'range', label: 'Date Range' }, { value: 'specific', label: 'Specific Dates' }].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, date_selection_type: opt.value }))}
                    className={`tab-btn ${formData.date_selection_type === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {formData.date_selection_type === 'range' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                  <DatePickerField
                    theme="employee"
                    selected={parseLocalDate(formData.start_date)}
                    onChange={d => setFormData(prev => ({ ...prev, start_date: toLocalISODate(d) }))}
                    placeholderText="Select start date"
                    minDate={new Date()}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Nightwork Date</label>
                  <DatePickerField
                    theme="employee"
                    selected={parseLocalDate(formData.end_date)}
                    onChange={d => setFormData(prev => ({ ...prev, end_date: toLocalISODate(d) }))}
                    placeholderText="Select end date"
                    minDate={parseLocalDate(formData.start_date) || new Date()}
                  />
                  <p className="text-xs text-gray-400 mt-1">The last date workers report for nightwork. Recovery day is the following day.</p>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Pick Dates</label>
                <DatePickerField
                  inline
                  theme="employee"
                  selected={null}
                  onChange={toggleSpecificDate}
                  highlightDates={formData.specific_dates.map(d => parseLocalDate(d))}
                  minDate={new Date()}
                />
                {formData.specific_dates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {formData.specific_dates.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-800 text-xs rounded-full ring-1 ring-inset ring-blue-600/20">
                        {new Date(d + 'T00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'Asia/Singapore' })}
                        <button type="button" onClick={() => toggleSpecificDate(parseLocalDate(d))} className="hover:text-blue-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Time Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Clock className="w-3.5 h-3.5 inline mr-1" />Start Time
                </label>
                <FormSelect
                  options={NIGHT_HOUR_OPTIONS}
                  value={formData.start_time}
                  onChange={v => setFormData(prev => ({ ...prev, start_time: v }))}
                  theme="employee"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Clock className="w-3.5 h-3.5 inline mr-1" />End Time
                </label>
                <FormSelect
                  options={NIGHT_HOUR_OPTIONS}
                  value={formData.end_time}
                  onChange={v => setFormData(prev => ({ ...prev, end_time: v }))}
                  theme="employee"
                />
              </div>
            </div>

            {/* Shift info banner */}
            {shiftInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p>
                    <strong>{shiftInfo.gross}h</strong> total shift &middot; <strong>{breakMinutes / 60}h</strong> mandatory break{breakMinutes >= 120 ? ` (${breakMinutes / 60} × 1h per MOM)` : ''} &middot; <strong>{shiftInfo.net}h</strong> net working hours
                  </p>
                  {extensionContinuity?.kind === 'gap' && (
                    <p className="mt-1 text-xs text-blue-600">
                      Extension mode: {extensionContinuity.gapHours}h gap between day-shift end (5 PM) and NW start counts as a meal break — break count is computed from the NW shift alone.
                    </p>
                  )}
                  {extensionContinuity?.kind === 'continuous_from_lunch' && (
                    <p className="mt-1 text-xs text-blue-600">
                      Extension mode: no real gap after the day shift, so the worker has been continuously working since lunch end (1 PM). Break count is computed against the {extensionContinuity.continuousHours}h continuous block (1 PM → NW end).
                    </p>
                  )}
                  {nightworkDateCount > 0 && (
                    <p className="mt-1 text-xs text-blue-600">
                      {nightworkDateCount} nightwork date{nightworkDateCount !== 1 ? 's' : ''} &middot; {nightworkDateCount} recovery day{nightworkDateCount !== 1 ? 's' : ''} (day after last shift)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Recovery Day — placed after time selection so TL can decide based on shift end time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recovery Day</label>
              <div className="flex gap-3">
                {[
                  { value: 'full_day', label: 'Full Day' },
                  { value: 'am_only', label: 'AM Only' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recovery_type: opt.value }))}
                    className={`tab-btn ${formData.recovery_type === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {formData.end_time && (
                <p className="text-xs text-gray-500 mt-1">
                  Shift ends at {formatTimeString12Hour(formData.end_time)}.
                  {formData.recovery_type === 'am_only'
                    ? ' Workers rest the morning, return after lunch.'
                    : ' Workers rest the entire following day.'}
                </p>
              )}
              {formData.nightwork_mode === 'standard' && formData.recovery_type === 'am_only' && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  AM-only recovery with standard mode — ensure workers have sufficient rest.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Personnel */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Users className="w-4 h-4 inline mr-1.5" />
                Workers / Workmen
              </label>
              <p className="text-xs text-gray-500 mb-2">Select workers for nightwork. Grouped by trade category.</p>
              <SearchableMultiCombobox
                items={flatWorkers}
                value={formData.worker_ids}
                onChange={ids => {
                  setFormData(prev => {
                    const overrides = { ...prev.worker_date_overrides };
                    Object.keys(overrides).forEach(k => { if (!ids.includes(k)) delete overrides[k]; });
                    return { ...prev, worker_ids: ids, worker_date_overrides: overrides };
                  });
                  setCheckedWorkers(prev => {
                    const next = new Set(prev);
                    prev.forEach(id => { if (!ids.includes(id)) next.delete(id); });
                    return next;
                  });
                }}
                displayFn={w => `${w.first_name} ${w.last_name}`}
                valueFn={w => w.staff_id}
                searchFn={(w, term) =>
                  `${w.first_name} ${w.last_name}`.toLowerCase().includes(term) ||
                  w.staff_id.toLowerCase().includes(term)
                }
                groupFn={w => w.trade_category}
                placeholder="Select workers..."
                searchPlaceholder="Search by name or staff ID..."
                emptyLabel="No workers found"
                loading={workersLoading}
                loadingLabel="Loading workers..."
                theme="employee"
                variant="form"
                showChips
                className="w-full"
                renderItem={(w) => (
                  <>
                    <span className="flex-1">{w.first_name} {w.last_name}</span>
                    <span className="text-xs text-gray-400 font-mono">{w.staff_id}</span>
                  </>
                )}
              />
            </div>

            {formData.worker_ids.length > 0 && (formData.start_date || formData.specific_dates.length > 0) && (() => {
              const allWorkers = (tradeGroups || []).flatMap(g => g.workers);
              const workerMap = {};
              allWorkers.forEach(w => { workerMap[w.staff_id] = w; });

              const masterLabel = formData.date_selection_type === 'range' && formData.start_date && formData.end_date
                ? `${formatDateShort(formData.start_date)} – ${formatDateShort(formData.end_date)}`
                : formData.specific_dates.length > 0
                  ? `${formData.specific_dates.length} dates`
                  : 'Full period';

              const getWorkerDateLabel = (staffId) => {
                const ov = formData.worker_date_overrides[staffId];
                if (!ov) return null;
                return `${formatDateShort(ov.start_date)} – ${formatDateShort(ov.end_date)}`;
              };

              const allChecked = formData.worker_ids.length > 0 && checkedWorkers.size === formData.worker_ids.length;

              const toggleAll = () => {
                if (allChecked) {
                  setCheckedWorkers(new Set());
                } else {
                  setCheckedWorkers(new Set(formData.worker_ids));
                }
              };

              const toggleCheck = (id) => {
                setCheckedWorkers(prev => {
                  const next = new Set(prev);
                  next.has(id) ? next.delete(id) : next.add(id);
                  return next;
                });
              };

              const masterStart = formData.date_selection_type === 'range' ? parseLocalDate(formData.start_date) : null;
              const masterEnd = formData.date_selection_type === 'range' ? parseLocalDate(formData.end_date) : null;

              const applyBulkDates = () => {
                if (!bulkStartDate || !bulkEndDate) return;
                const startISO = toLocalISODate(bulkStartDate);
                const endISO = toLocalISODate(bulkEndDate);
                const isMasterRange = startISO === formData.start_date && endISO === formData.end_date;
                setFormData(prev => {
                  const overrides = { ...prev.worker_date_overrides };
                  checkedWorkers.forEach(id => {
                    if (isMasterRange) {
                      delete overrides[id];
                    } else {
                      overrides[id] = { start_date: startISO, end_date: endISO };
                    }
                  });
                  return { ...prev, worker_date_overrides: overrides };
                });
                setShowBulkDatePicker(false);
                setCheckedWorkers(new Set());
                setBulkStartDate(null);
                setBulkEndDate(null);
              };

              const clearOverride = (staffId) => {
                setFormData(prev => {
                  const overrides = { ...prev.worker_date_overrides };
                  delete overrides[staffId];
                  return { ...prev, worker_date_overrides: overrides };
                });
              };

              const hasOverrides = Object.keys(formData.worker_date_overrides).length > 0;

              return (
                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarRange className="w-4 h-4 text-blue-800" />
                      <span className="text-sm font-semibold text-gray-900">Assign Dates</span>
                      <span className="text-xs text-gray-500">({formData.worker_ids.length} worker{formData.worker_ids.length !== 1 ? 's' : ''})</span>
                    </div>
                    {hasOverrides && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {Object.keys(formData.worker_date_overrides).length} custom
                      </span>
                    )}
                  </div>

                  <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500 w-3.5 h-3.5"
                      />
                      Select all
                    </label>
                    {checkedWorkers.size > 0 && (
                      <span className="text-xs text-blue-800 font-medium ml-auto">
                        {checkedWorkers.size} selected
                      </span>
                    )}
                  </div>

                  <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {formData.worker_ids.map(id => {
                      const w = workerMap[id];
                      const name = w ? `${w.first_name} ${w.last_name}` : id;
                      const trade = w?.trade_category || '';
                      const customDate = getWorkerDateLabel(id);
                      const isChecked = checkedWorkers.has(id);
                      return (
                        <div
                          key={id}
                          className={`flex items-center gap-2 px-3 py-2 ${isChecked ? 'bg-blue-50/50' : 'hover:bg-white'} transition-colors`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleCheck(id)}
                            className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500 w-3.5 h-3.5 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-900 truncate block">{name}</span>
                            {trade && <span className="text-xs text-gray-400">{trade}</span>}
                          </div>
                          {customDate ? (
                            <button
                              type="button"
                              onClick={() => clearOverride(id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                              title="Custom dates — click to reset to full period"
                            >
                              {customDate}
                              <X className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {masterLabel}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {checkedWorkers.size > 0 && (
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3">
                      {!showBulkDatePicker ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowBulkDatePicker(true);
                            setBulkStartDate(masterStart);
                            setBulkEndDate(masterEnd);
                          }}
                          className="btn btn-primary w-full text-sm"
                        >
                          <CalendarRange className="w-4 h-4" />
                          Set Dates for {checkedWorkers.size} Worker{checkedWorkers.size !== 1 ? 's' : ''}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Custom Date Range</span>
                            <button
                              type="button"
                              onClick={() => { setShowBulkDatePicker(false); setBulkStartDate(null); setBulkEndDate(null); }}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <DatePickerField
                                theme="employee"
                                showIcon={false}
                                selected={bulkStartDate}
                                onChange={d => setBulkStartDate(d)}
                                selectsStart
                                startDate={bulkStartDate}
                                endDate={bulkEndDate}
                                minDate={masterStart}
                                maxDate={masterEnd}
                                placeholderText="Start"
                                className="input w-full text-sm"
                              />
                            </div>
                            <span className="text-gray-400 text-sm">to</span>
                            <div className="flex-1">
                              <DatePickerField
                                theme="employee"
                                showIcon={false}
                                selected={bulkEndDate}
                                onChange={d => setBulkEndDate(d)}
                                selectsEnd
                                startDate={bulkStartDate}
                                endDate={bulkEndDate}
                                minDate={bulkStartDate || masterStart}
                                maxDate={masterEnd}
                                placeholderText="End"
                                className="input w-full text-sm"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={applyBulkDates}
                            disabled={!bulkStartDate || !bulkEndDate}
                            className="btn btn-primary w-full text-sm"
                          >
                            <Check className="w-4 h-4" />
                            Apply to {checkedWorkers.size} Worker{checkedWorkers.size !== 1 ? 's' : ''}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Shield className="w-4 h-4 inline mr-1.5" />
                Safety Personnel
              </label>
              <p className="text-xs text-gray-500 mb-2">Assign safety officers if applicable for this nightwork scope.</p>
              <SafetyPersonnelSelect
                personnel={safetyPersonnel}
                selected={formData.safety_ids}
                onChange={ids => setFormData(prev => ({ ...prev, safety_ids: ids }))}
                loading={safetyLoading}
              />
            </div>
          </div>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Moon className="w-4 h-4 text-blue-800" />
              {isPendingEdit ? 'Review Changes' : isEditMode ? 'Amendment Summary' : 'Request Summary'}
            </h3>
            {isPendingEdit && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <p className="text-sm text-blue-800">
                  This request is pending approval. Changing the description only keeps the current approval
                  progress; changing dates, times, personnel, mode, recovery, or project resets approvals and
                  re-enters the approval chain.
                </p>
              </div>
            )}

            <SummarySection label="Project" value={formData.project_name} />
            <SummarySection label="Description" value={formData.description} multiline />
            <SummarySection
              label="Mode"
              value={formData.nightwork_mode === 'extension' ? 'Extension (after regular hours)' : 'Standard (replaces day shift)'}
            />
            <SummarySection
              label="Recovery"
              value={formData.recovery_type === 'am_only' ? 'AM Only — return after lunch' : 'Full Day — rest the entire day'}
            />
            <SummarySection
              label="Dates"
              value={
                formData.date_selection_type === 'range'
                  ? `${formatDateRange(formData.start_date, formData.end_date)} (${nightworkDateCount} night${nightworkDateCount !== 1 ? 's' : ''})`
                  : `${formData.specific_dates.length} specific dates`
              }
            />
            <SummarySection label="Shift Time" value={`${formatTimeString12Hour(formData.start_time)} – ${formatTimeString12Hour(formData.end_time)} (${shiftInfo?.net}h net)`} />

            {/* Workers grouped by date range */}
            {(() => {
              const allWorkers = (tradeGroups || []).flatMap(g => g.workers);
              const workerMap = {};
              allWorkers.forEach(w => { workerMap[w.staff_id] = w; });

              const groups = {};
              formData.worker_ids.forEach(id => {
                const ov = formData.worker_date_overrides[id];
                const key = ov ? `${ov.start_date}|${ov.end_date}` : 'full';
                if (!groups[key]) {
                  groups[key] = {
                    label: ov
                      ? `${formatDateShort(ov.start_date)} – ${formatDateShort(ov.end_date)}`
                      : formData.date_selection_type === 'range'
                        ? `${formatDateShort(formData.start_date)} – ${formatDateShort(formData.end_date)}`
                        : `${formData.specific_dates.length} dates`,
                    isCustom: !!ov,
                    workers: [],
                  };
                }
                const w = workerMap[id];
                groups[key].workers.push(w ? { name: `${w.first_name} ${w.last_name}`, staffId: w.staff_id, trade: w.trade_category } : { name: id, staffId: id, trade: '' });
              });

              const groupList = Object.values(groups).sort((a, b) => a.isCustom - b.isCustom);

              return (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">Workers ({formData.worker_ids.length})</div>
                  <div className="space-y-2">
                    {groupList.map((g, i) => (
                      <div key={i} className={`rounded-xl border px-3 py-2 ${g.isCustom ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${g.isCustom ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-800'}`}>
                            {g.label}
                          </span>
                          <span className="text-xs text-gray-500">{g.workers.length} worker{g.workers.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-1">
                          {g.workers.map((w, wi) => (
                            <div key={wi} className="flex items-baseline gap-2">
                              <span className="text-sm text-gray-900 font-medium">{w.name}</span>
                              <span className="text-xs text-gray-400 font-mono">{w.staffId}</span>
                              {w.trade && <span className="text-xs text-gray-500">{w.trade}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Safety — show actual names (only when assigned) */}
            {formData.safety_ids.length > 0 && (() => {
              const safetyMap = {};
              (safetyPersonnel || []).forEach(p => { safetyMap[p.staff_id] = p; });
              return (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">Safety ({formData.safety_ids.length})</div>
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2">
                    <div className="space-y-1">
                      {formData.safety_ids.map(id => {
                        const p = safetyMap[id];
                        return (
                          <div key={id} className="flex items-baseline gap-2">
                            <span className="text-sm text-gray-900 font-medium">{p ? `${p.first_name} ${p.last_name}` : id}</span>
                            <span className="text-xs text-gray-400 font-mono">{id}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {!canSubmit && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-orange-700">Please complete all required fields before submitting.</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-4 border-t border-gray-100">
          <div>
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)} className="btn btn-secondary">
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step < totalSteps ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="btn btn-primary"
                disabled={
                  (step === 1 && !isStep1Valid) ||
                  (step === 2 && !isStep2Valid) ||
                  (step === 3 && !isStep3Valid)
                }
              >
                Continue
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || saveMutation.isPending || submitMutation.isPending}
                  className="btn btn-primary"
                >
                  <Send className="w-4 h-4" />
                  {saveMutation.isPending
                    ? 'Saving...'
                    : submitMutation.isPending
                      ? 'Submitting...'
                      : isPendingEdit ? 'Save Changes' : isEditMode ? 'Submit Amendment' : 'Submit for Approval'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SummarySection = ({ label, value, multiline }) => (
  <div className="flex gap-4">
    <span className="text-xs font-medium text-gray-500 w-24 flex-shrink-0 pt-0.5">{label}</span>
    <span className={`text-sm text-gray-900 ${multiline ? 'whitespace-pre-wrap' : ''}`}>{value || '—'}</span>
  </div>
);

export default NightworkRequestForm;
